import React, { useEffect, useState, useRef, useCallback, Suspense, lazy } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { init } from "@noriginmedia/norigin-spatial-navigation";
import { Menu } from "../layout/sidebar";
import SlideAnimationFrame from "../common/slideAnimationFrame";
import { useDispatch, useSelector } from "react-redux";
import {
  resetAuth,
  setActiveSeciton,
  setPhone,
} from "../../redux/Slices/auth.slice";
import httpService from "../../services/http.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAuthApiRoute } from "../../utils/api.utils";
import toast from "react-hot-toast";

// Lazy load login step components for code splitting
var LoginPageContent = lazy(function () { return import("./loginPageStep1"); });
var LoginVerifyPageContent = lazy(function () { return import("./loginPageStep2"); });
var SelectProfileView = lazy(function () { return import("./loginPageStep3"); });
var SessionManagementModal = lazy(function () { return import("./SessionManagementModal"); });

// Initialize spatial navigation
init({
  debug: false,
  visualDebug: false,
  rtl: true,
});

// Global styles
var GlobalStyle = createGlobalStyle`
  ::-webkit-scrollbar {
    display: none;
  }
`;

// App container using legacy CSS (table layout instead of flexbox)
var AppContainer = styled.div`
  background: #121212;
  width: 100vw;
  height: 100vh;
  display: table;
  table-layout: fixed;
  direction: rtl;
`;

var SidebarCell = styled.div`
  display: table-cell;
  width: 250px;
  min-width: 250px;
  max-width: 250px;
  vertical-align: top;
  height: 100vh;
`;

var ContentCell = styled.div`
  display: table-cell;
  width: calc(100vw - 250px);
  vertical-align: middle;
  text-align: center;
  position: relative;
  height: 100vh;
  overflow: hidden;
`;

// Shared Card wrapper for all login slides
var MainCard = styled.div`
  display: inline-block;
  width: 85%;
  max-width: 1600px;
  height: 85%;
  max-height: 85vh;
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.85) 0%, rgba(20, 20, 20, 0.9) 100%);
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  position: relative;
`;

// Loading fallback
var LoadingFallback = styled.div`
  display: table;
  width: 100%;
  height: 100vh;
  table-layout: fixed;
  background: #121212;
`;

var LoadingCell = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
`;

var Spinner = styled.div`
  display: inline-block;
  width: 48px;
  height: 48px;
  border: 4px solid rgba(106, 0, 0, 0.3);
  border-top-color: #6A0000;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Fallback component for Suspense
function LoadingSpinner() {
  return (
    <LoadingFallback>
      <LoadingCell>
        <Spinner />
      </LoadingCell>
    </LoadingFallback>
  );
}

// Storage keys for Login Attempt Token (LAT)
var LAT_STORAGE_KEYS = {
  TOKEN: "webTvLoginAttemptToken",
  EXPIRY: "webTvLoginAttemptExpiry",
};

/**
 * LAT Storage helper
 */
var LoginAttemptStorage = {
  setToken: function (token, expiresAt) {
    if (token && typeof token === 'string') {
      localStorage.setItem(LAT_STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(LAT_STORAGE_KEYS.EXPIRY, String(expiresAt));
    }
  },

  getToken: function () {
    var token = localStorage.getItem(LAT_STORAGE_KEYS.TOKEN);
    if (!token || token === "undefined" || token === "null" || token === "") {
      return null;
    }
    return token;
  },

  getTokenExpiry: function () {
    var expiry = localStorage.getItem(LAT_STORAGE_KEYS.EXPIRY);
    if (!expiry || expiry === "undefined" || expiry === "null") {
      return null;
    }
    return parseInt(expiry, 10);
  },

  isTokenExpired: function () {
    var expiry = this.getTokenExpiry();
    if (!expiry) return true;
    return Date.now() > expiry;
  },

  isTokenValid: function () {
    var token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired();
  },

  clear: function () {
    localStorage.removeItem(LAT_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(LAT_STORAGE_KEYS.EXPIRY);
  },
};

// Maximum sessions allowed (block when user has 4 or more)
var MAX_SESSIONS = 4;

export default function SpitialLogin() {
  var [isLoading, setLoading] = useState(false);
  var { phoneNumber, activeSection } = useSelector(function (state) { return state.Auth; });
  var [otp, setOTP] = React.useState(Array.from({ length: 5 }));
  var [profiles, setProfiles] = useState([]);
  var [sessions, setSessions] = useState([]);
  var [isDeletingSession, setIsDeletingSession] = useState(false);
  var [removingSessionId, setRemovingSessionId] = useState(null);
  var dispatch = useDispatch();
  var navigate = useNavigate();
  var [searchParams] = useSearchParams();
  var tokenGenerationInProgress = useRef(false);

  /**
   * Generate Login Attempt Token
   */
  var generateLoginToken = useCallback(async function () {
    if (tokenGenerationInProgress.current) {
      return LoginAttemptStorage.getToken();
    }

    try {
      tokenGenerationInProgress.current = true;
      var userAgent = navigator.userAgent;

      var response = await httpService.post(
        getAuthApiRoute("generate-login-token"),
        { userAgent: userAgent }
      );

      if (response.status === 200) {
        var responseData = response.data;
        var data = responseData.data || responseData;
        var token = data.token;
        var expiresAt = data.expiresAt || (Date.now() + 5 * 60 * 1000);

        if (!token || typeof token !== 'string') {
          return null;
        }

        LoginAttemptStorage.setToken(token, expiresAt);
        return token;
      }

      return null;
    } catch (error) {
      return null;
    } finally {
      tokenGenerationInProgress.current = false;
    }
  }, []);

  /**
   * Get a valid LAT token - generates if needed
   */
  var getValidToken = useCallback(async function () {
    var existingToken = LoginAttemptStorage.getToken();
    if (existingToken && LoginAttemptStorage.isTokenValid()) {
      return existingToken;
    }
    return await generateLoginToken();
  }, [generateLoginToken]);

  /**
   * Initialize LAT on mount and reset activeSection
   */
  useEffect(function () {
    dispatch(setActiveSeciton(0));
    if (!LoginAttemptStorage.isTokenValid()) {
      generateLoginToken();
    }
  }, [generateLoginToken, dispatch]);

  var handlePhoneNumber = function (e) {
    if (phoneNumber?.length === 11 && e !== "delete") {
      return;
    } else if (e === "delete") {
      dispatch(setPhone(phoneNumber?.slice(0, -1)));
    } else {
      dispatch(setPhone(phoneNumber?.toString() + e.toString()));
    }
  };

  /**
   * Request OTP - with automatic token regeneration on expiry
   */
  var requestOTPVerify = async function (isRetry) {
    try {
      if (!phoneNumber) return toast.error("شماره تلفن الزامی میباشد");
      if (phoneNumber.length !== 11)
        return toast.error("شماره تلفن وارد شده صحیح نیست");
      if (isLoading) return toast("لطفا صبر کنید");

      var regex = /^09\d{9}$/;
      if (!regex.test(phoneNumber)) {
        return toast.error("شماره تلفن وارد شده صحیح نیست");
      }

      setLoading(true);
      var token = await getValidToken();

      if (!token || typeof token !== 'string' || token === "undefined" || token === "null") {
        toast.error("خطا در ایجاد توکن امنیتی، لطفا صفحه را رفرش کنید");
        setLoading(false);
        return;
      }

      var response = await httpService.post(
        getAuthApiRoute("send-login-otp"),
        {
          mobileNumber: Number(phoneNumber),
          loginAttemptToken: token,
        }
      );

      if (response.status === 200) {
        toast.success("کد تایید برای شما ارسال شد");
        setLoading(false);
        dispatch(setActiveSeciton(1));
      } else {
        toast.error(response.data?.message || "خطایی رخ داده است");
        setLoading(false);
      }
    } catch (error) {
      var errorMsg = error.response?.data?.message || error.message || "خطایی رخ داده است";
      var statusCode = error.response?.status || error.response?.data?.statusCode;

      // Handle token expiry - auto regenerate and retry once
      if ((statusCode === 401 || errorMsg.toLowerCase().includes("expired") || errorMsg.includes("consumed")) && !isRetry) {
        LoginAttemptStorage.clear();
        // Generate new token and retry
        var newToken = await generateLoginToken();
        if (newToken) {
          setLoading(false);
          return requestOTPVerify(true);
        } else {
          toast.error("خطا در ایجاد توکن جدید، لطفا صفحه را رفرش کنید");
          setLoading(false);
          return;
        }
      }

      if (errorMsg.includes("User-Agent mismatch") || errorMsg.includes("token theft")) {
        LoginAttemptStorage.clear();
        toast.error("خطای امنیتی - لطفا دوباره تلاش کنید");
      } else {
        toast.error(errorMsg);
      }
      setLoading(false);
    }
  };

  var handleOTP = function (e) {
    var index = otp.findIndex(function (item) {
      return item === undefined || item === "";
    });
    if (index === -1 && e === "delete") index = 5;
    if (e === "delete") {
      var newOTP = [...otp];
      newOTP[index - 1] = "";
      setOTP(newOTP);
    } else if (e === "submit") {
      // submit action
    } else {
      var newOTP2 = [...otp];
      newOTP2[index] = e;
      setOTP(newOTP2);
    }
  };

  /**
   * Verify OTP - with automatic token regeneration on expiry
   */
  var verifyCode = async function (isRetry) {
    try {
      if (otp.filter(function (x) { return x === undefined || x === ""; }).length > 0)
        return toast.error("کد وارد شده صحیح نیست");
      if (isLoading) return toast("لطفا صبر کنید");

      setLoading(true);

      // Check local token validity first
      var token = LoginAttemptStorage.getToken();
      if (!token || LoginAttemptStorage.isTokenExpired()) {
        // Token expired locally - try to regenerate and retry
        if (!isRetry) {
          LoginAttemptStorage.clear();
          var newToken = await generateLoginToken();
          if (newToken) {
            setLoading(false);
            toast.info("توکن جدید ایجاد شد، لطفا دوباره کد را ارسال کنید");
            dispatch(setActiveSeciton(0));
            return;
          }
        }
        toast.error("نشست منقضی شده، لطفا دوباره تلاش کنید");
        setLoading(false);
        dispatch(setActiveSeciton(0));
        return;
      }

      var response = await httpService.post(
        getAuthApiRoute("login-register"),
        {
          mobileNumber: Number(phoneNumber),
          otp: Number(otp.join("")),
          loginAttemptToken: token,
        }
      );

      if (response.status === 200) {
        var responseData = response.data;
        var data = responseData.data || responseData;
        var refreshToken = data.refreshToken;
        var accessToken = data.accessToken;

        localStorage.setItem("webTvrefreshToken", refreshToken);
        localStorage.setItem("webTvUserToken", accessToken);

        // Check session count BEFORE fetching profiles
        try {
          var sessionsResponse = await httpService.get(
            getAuthApiRoute("sessions"),
            {
              headers: {
                Authorization: "Bearer " + accessToken,
              },
            }
          );

          if (sessionsResponse.status === 200) {
            var sessionsData = sessionsResponse.data.data || sessionsResponse.data;
            var sessionsList = sessionsData.sessions || sessionsData || [];

            if (sessionsList.length >= MAX_SESSIONS) {
              setSessions(sessionsList);
              dispatch(setActiveSeciton(3));
              setLoading(false);
              return;
            }
          }
        } catch (sessionError) {
          // Continue anyway
        }

        // Fetch profiles
        var profileResponse = await httpService.get(getAuthApiRoute("user"), {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        });

        if (profileResponse.status === 200) {
          var profileData = profileResponse.data.data || profileResponse.data;
          setProfiles(profileData.profiles);
          dispatch(setActiveSeciton(2));
        } else {
          toast.error("خطا در دریافت پروفایل ها");
          dispatch(resetAuth());
          revertStep();
        }
      } else {
        toast.error(response.data?.message || "خطایی رخ داده است");
      }
    } catch (error) {
      var errorMsg = error.response?.data?.message || error.message || "خطایی رخ داده است";
      var statusCode = error.response?.status || error.response?.data?.statusCode;

      // Handle token expiry from server - generate new token and go back to phone entry
      if ((statusCode === 401 || errorMsg.toLowerCase().includes("expired") || errorMsg.includes("consumed")) && !isRetry) {
        LoginAttemptStorage.clear();
        // Generate new token for next attempt
        await generateLoginToken();
        toast.error("توکن منقضی شده، لطفا دوباره شماره تلفن را وارد کنید");
        setOTP(Array.from({ length: 5 }));
        dispatch(setActiveSeciton(0));
        setLoading(false);
        return;
      }

      if (errorMsg.includes("User-Agent mismatch") || errorMsg.includes("token theft")) {
        LoginAttemptStorage.clear();
        toast.error("خطای امنیتی - لطفا دوباره تلاش کنید");
        dispatch(setActiveSeciton(0));
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Select Profile
   */
  var handleSelectProfile = async function (profileId) {
    try {
      if (isLoading) return toast("لطفا صبر کنید");
      setLoading(true);

      var qrTempToken = localStorage.getItem("webTvQrTempToken");
      var lat = LoginAttemptStorage.getToken();
      var response;

      if (qrTempToken) {
        response = await httpService.post(
          getAuthApiRoute("qr/select-profile"),
          {
            profileId: profileId,
            loginAttemptToken: lat,
          },
          {
            headers: {
              Authorization: "Bearer " + qrTempToken,
            },
          }
        );
      } else {
        response = await httpService.post(
          getAuthApiRoute("select-profile"),
          {
            profileId: profileId,
            loginAttemptToken: lat,
          }
        );
      }

      if (response.status === 200) {
        var data = response.data.data || response.data;
        var accessToken = data.accessToken;
        var refreshToken = data.refreshToken;

        if (!accessToken) {
          toast.error("خطا در دریافت توکن");
          setLoading(false);
          return;
        }

        localStorage.removeItem("webTvQrTempToken");
        LoginAttemptStorage.clear();

        localStorage.setItem("webTvUserToken", accessToken);
        if (refreshToken) {
          localStorage.setItem("webTvrefreshToken", refreshToken);
        }

        toast.success("به منه باخ خوش آمدید");
        dispatch(resetAuth());

        var redirect = searchParams.get("ref");
        if (redirect) {
          navigate(decodeURIComponent(redirect), { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } else {
        toast.error(response.data?.message || "خطایی رخ داده است");
        setLoading(false);
      }
    } catch (error) {
      var errorMsg = error.response?.data?.message || error.message || "";

      if (errorMsg.includes("Maximum session limit") || errorMsg.includes("حداکثر تعداد نشست")) {
        await fetchSessions();
        dispatch(setActiveSeciton(3));
        toast.error("حداکثر تعداد نشست‌های مجاز رسیده است. لطفا یکی را حذف کنید.");
      } else {
        toast.error(errorMsg || "خطایی رخ داده است");
      }
      setLoading(false);
    }
  };

  /**
   * Fetch active sessions
   */
  var fetchSessions = async function () {
    try {
      var accessToken = localStorage.getItem("webTvUserToken");
      var response = await httpService.get(
        getAuthApiRoute("sessions"),
        {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        }
      );

      if (response.status === 200) {
        var data = response.data.data || response.data;
        var sessionsList = data.sessions || data || [];
        setSessions(sessionsList);
      }
    } catch (error) {
      toast.error("خطا در دریافت نشست‌های فعال");
    }
  };

  /**
   * Delete a session using authorization token
   * Uses DELETE /session endpoint with authorization header
   * This matches the Flutter implementation: deleteSession()
   */
  var handleDeleteSession = async function (sessionId) {
    try {
      if (isDeletingSession) return;
      setIsDeletingSession(true);
      setRemovingSessionId(sessionId);

      // Get the authorization token
      var accessToken = localStorage.getItem("webTvQrTempToken") || localStorage.getItem("webTvUserToken");

      if (!accessToken) {
        toast.error("توکن یافت نشد");
        setIsDeletingSession(false);
        setRemovingSessionId(null);
        return;
      }

      // Use DELETE /session endpoint with authorization header and sessionId in body
      // This matches Flutter: deleteSession(authorization, sessionId)
      var response = await httpService.delete(
        getAuthApiRoute("session"),
        {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
          data: { sessionId: sessionId },
        }
      );

      if (response.status === 204) {
        toast.success("نشست با موفقیت حذف شد");

        var newSessions = sessions.filter(function (s) {
          return (s._id || s.sessionId) !== sessionId;
        });
        setSessions(newSessions);

        // If sessions are now under the limit, proceed to profile selection
        if (newSessions.length < MAX_SESSIONS) {
          try {
            var profileResponse = await httpService.get(getAuthApiRoute("user"), {
              headers: {
                Authorization: "Bearer " + accessToken,
              },
            });

            if (profileResponse.status === 200) {
              var profileData = profileResponse.data.data || profileResponse.data;
              setProfiles(profileData.profiles);
            }
          } catch (profileError) {
            // Profile fetch failed, but we can still proceed
            console.error("Failed to fetch profiles:", profileError);
          }
          // Always redirect to profile selection screen
          dispatch(setActiveSeciton(2));
        }
      } else {
        toast.error(response.data?.message || "خطا در حذف نشست");
      }
    } catch (error) {
      var errorMsg = error.response?.data?.message || "خطا در حذف نشست";
      toast.error(errorMsg);
    } finally {
      setIsDeletingSession(false);
      setRemovingSessionId(null);
    }
  };

  /**
   * Handle session modal close/cancel - return to phone input
   */
  var handleSessionModalClose = function () {
    dispatch(setActiveSeciton(0));
  };

  /**
   * Handle session removal request from modal
   */
  var handleRemoveSession = async function (sessionId) {
    await handleDeleteSession(sessionId);
  };

  var revertStep = function () {
    dispatch(setActiveSeciton(0));
  };

  /**
   * Handle QR Login Success
   */
  var handleQRLoginSuccess = async function (tokenData) {
    try {
      setLoading(true);

      if (tokenData.isTemporary && tokenData.temporaryToken) {
        localStorage.setItem("webTvQrTempToken", tokenData.temporaryToken);

        try {
          var sessionsResponse = await httpService.get(
            getAuthApiRoute("sessions"),
            {
              headers: {
                Authorization: "Bearer " + tokenData.temporaryToken,
              },
            }
          );

          if (sessionsResponse.status === 200) {
            var sessionsData = sessionsResponse.data.data || sessionsResponse.data;
            var sessionsList = sessionsData.sessions || sessionsData || [];

            if (sessionsList.length >= MAX_SESSIONS) {
              setSessions(sessionsList);
              dispatch(setActiveSeciton(3));
              setLoading(false);
              return;
            }
          }
        } catch (sessionError) {
          // Continue anyway
        }

        var profileResponse = await httpService.get(getAuthApiRoute("user"), {
          headers: {
            Authorization: "Bearer " + tokenData.temporaryToken,
          },
        });

        if (profileResponse.status === 200) {
          var data = profileResponse.data.data || profileResponse.data;
          setProfiles(data.profiles);
          toast.success("پروفایل خود را انتخاب کنید");
          dispatch(setActiveSeciton(2));
        } else {
          toast.error("خطا در دریافت پروفایل ها");
        }
        return;
      }

      if (tokenData.accessToken) {
        localStorage.setItem("webTvUserToken", tokenData.accessToken);
        if (tokenData.refreshToken) {
          localStorage.setItem("webTvrefreshToken", tokenData.refreshToken);
        }

        try {
          var sessResp = await httpService.get(
            getAuthApiRoute("sessions"),
            {
              headers: {
                Authorization: "Bearer " + tokenData.accessToken,
              },
            }
          );

          if (sessResp.status === 200) {
            var sessData = sessResp.data.data || sessResp.data;
            var sessList = sessData.sessions || sessData || [];

            if (sessList.length >= MAX_SESSIONS) {
              setSessions(sessList);
              dispatch(setActiveSeciton(3));
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          // Continue anyway
        }

        var profResp = await httpService.get(getAuthApiRoute("user"), {
          headers: {
            Authorization: "Bearer " + tokenData.accessToken,
          },
        });

        if (profResp.status === 200) {
          var profData = profResp.data.data || profResp.data;
          setProfiles(profData.profiles);
          toast.success("ورود موفق");
          dispatch(setActiveSeciton(2));
        } else {
          toast.error("خطا در دریافت پروفایل ها");
        }
        return;
      }

      toast.error("خطا در دریافت اطلاعات ورود");
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "خطا");
    } finally {
      setLoading(false);
    }
  };

  // Define steps with lazy-loaded components wrapped in Suspense
  var steps = [
    {
      id: "login-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <LoginPageContent
            isLoading={isLoading}
            handlePhoneNumber={handlePhoneNumber}
            requestOTPVerify={requestOTPVerify}
            handleQRLoginSuccess={handleQRLoginSuccess}
            activeSection={activeSection}
          />
        </Suspense>
      ),
    },
    {
      id: "otp-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <LoginVerifyPageContent
            isLoading={isLoading}
            handleOTP={handleOTP}
            otp={otp}
            verifyCode={verifyCode}
            revertStep={revertStep}
          />
        </Suspense>
      ),
    },
    {
      id: "select-profile-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <SelectProfileView
            isLoading={isLoading}
            handleSelectProfile={handleSelectProfile}
            profiles={profiles}
          />
        </Suspense>
      ),
    },
    {
      id: "session-management-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <SessionManagementModal
            open={true}
            sessions={sessions}
            onRemoveSession={handleRemoveSession}
            onCancel={handleSessionModalClose}
            isRemoving={isDeletingSession}
            removingSessionId={removingSessionId}
          />
        </Suspense>
      ),
    },
  ];

  // Reset OTP when navigating back to login step
  useEffect(function () {
    if (activeSection === 0) {
      setOTP(Array.from({ length: 5 }));
    }
  }, [activeSection]);

  return (
    <AppContainer>
      <GlobalStyle />
      <SidebarCell>
        <Menu focusKey="MENU" />
      </SidebarCell>
      <ContentCell>
        <MainCard>
          <SlideAnimationFrame
            activeSectionIndex={activeSection}
            sections={steps}
          />
        </MainCard>
      </ContentCell>

    </AppContainer>
  );
}
